/* ================================================================
 * Tour stop · `as const` and the "data not code" principle
 * ----------------------------------------------------------------
 * See docs/code-roadmap.md §3.3 (TypeScript idioms) and §5 (sim)
 * for the surrounding narrative.
 *
 * Every balance number in the game lives in this file. Rink size,
 * skater acceleration, puck drag, goalie reaction ticks, AI skill
 * weights, period length, switching hysteresis. Nothing is sprinkled
 * across systems; every system imports `tuning` and reads from it.
 * AGENTS.md calls this out as a hard rule: "Balance constants live
 * in state/tuning.ts. Do not scatter numbers across systems."
 *
 * The TypeScript trick at the bottom — `as const` — is what makes
 * this work cleanly:
 *
 *   export const tuning = { ... } as const;
 *   export type Tuning = typeof tuning;
 *
 * Without `as const`, TypeScript would infer
 *
 *   tuning.rink.length: number
 *
 * because property values of an `object` default to their widened
 * primitive type. With `as const`, it infers
 *
 *   tuning.rink.length: 60
 *
 * — the LITERAL value, not the broader type. That makes every field
 * a compile-time constant from the type checker's point of view,
 * and lets us synthesise the entire `Tuning` type with one line of
 * `typeof`. If we hand-wrote the type, it would be 100 lines and
 * would drift the moment someone added a field.
 *
 * Why bother? Because tuning is data that other code can statically
 * reason about. The AI skill weights are exposed as a strongly-typed
 * tree under `tuning.ai.default`. Phase D will hash this whole object
 * into a `tuningHash` recorded with replays, so re-running a replay
 * with different tuning fails loudly instead of silently re-balancing.
 *
 * Changing values here:
 *   - Adding a new key requires no type plumbing. `typeof tuning`
 *     picks it up automatically; downstream consumers compile-error
 *     until they handle it.
 *   - Editing an existing value will change deterministic behaviour
 *     and the golden-hash tests will fail. If the change is
 *     intentional, re-pin the hash and call it out in the commit.
 *     See CLAUDE.md "how to verify your changes."
 * ================================================================ */

// Single source of truth for all balance constants. Renaming or changing
// any value here SHOULD bump tuningHash in recorded replays (Phase D).
//
// Units: 1 unit = 1 meter, 1 tick = 1/60 s. Rink dims match IIHF 60m×30m.
// Speeds are m/s, accelerations m/s². Real-world NHL reference points are
// noted inline where a value is deliberately compressed for arcade feel.

export const tuning = {
  rink: {
    length: 60,
    width: 30,
    cornerRadius: 5,
    goalLineOffset: 3.6,
    goalMouthHalfWidth: 1.8,
    goalDepth: 1.2,
  },
  player: {
    radius: 0.65,
    maxSpeed: 8.8, // NHL elite ~9–10.4 m/s; compressed slightly for readable rink scale
    turboMaxSpeed: 11.4, // arcade burst speed; visual trails/camera keep this readable
    acceleration: 26, // NHL '94-style snap: reach useful speed quickly
    brake: 36, // hard edge bite when stopping or reversing
    iceDrag: 0.988,
    faceSlewRate: 12, // rad/s
    staminaDepleteRate: 1.0,
    staminaRecoverRate: 0.4,
    staminaLockoutThreshold: 0.25,
    protectMaxSpeedMultiplier: 0.5,
  },
  puck: {
    radius: 0.22,
    drag: 0.992,
    lowSpeedDrag: 0.94,
    lowSpeedThreshold: 1.5,
    controlAnchorDistance: 0.9,
    possessionMinQuality: 0.25,
    noRecaptureTicks: 8,
    /**
     * Faceoff centre-lockout window. After a faceoff pop, both
     * centres are blocked from claiming the puck for this many ticks
     * — the "tied up in the draw" period. 60 ticks ≈ 1 second:
     * enough for the puck to clear the centres' stick zones and for
     * the targeted teammate to skate over.
     */
    faceoffCenterLockoutTicks: 60,
    /**
     * Slice 4 — same-opponent recapture lockout. After player A strips
     * or picks up the puck from B, B is blocked from re-claiming for
     * this many ticks. Breaks the A→B→A→B centre-ice ping-pong cycle
     * without slowing legitimate puck-recoveries by uninvolved players.
     * 18 ticks ≈ 300 ms.
     */
    sameOpponentRecaptureTicks: 18,
    swept: {
      substepSpeedThreshold: 12,
      maxSubsteps: 4,
    },
  },
  pass: {
    // Real NHL passes are ~12–20 m/s; compressed so receivers have time to react on a 2D screen.
    speedMin: 4,
    speedMax: 7.5,
    holdMaxTicks: 30,
    aimDominatesThreshold: 0.6,
    leadSeconds: 0.3,
    interceptBaseProbability: 0.55,
  },
  shot: {
    // Shot speeds are compressed ~50% vs. real NHL so the puck is trackable on a 2D screen.
    quickSpeed: 16, // wrist/snap; real NHL 27–36 m/s
    chargedSpeed: 22, // slap; real NHL 36–48 m/s (record ~48.6, Chara)
    chargeMaxTicks: 40,
    /**
     * While a Shot-mode puck stays above this speed, no stick zone
     * (teammate or opponent) picks it up — the shot flies past skaters
     * in the lane to the goalie / boards / net. Sits above max pass
     * speed (7.5) and max skater speed (6); a real shot decays below it
     * only after travelling well past typical shooting range.
     */
    activeMinSpeed: 8.0,
    oneTimerWindowTicks: 12,
    oneTimerSpeedBonus: 0.15,
    oneTimerAccuracyPenalty: 0.4,
  },
  goal: {
    /**
     * Slice 7 — Wraparound goal handling. Controlled-mode goals are
     * blocked unless the carrier's body is closing on the goal line at
     * this speed or higher. Filters out the "spin-on-stick own goal"
     * artifact (stationary carrier rotating stickDir) while allowing a
     * wraparound or stuff-in (carrier driving the net).
     */
    controlledMinClosingSpeed: 1.0,
  },
  check: {
    activeTicks: 8,
    recoveryTicks: 40,
    landStunTicks: 24,
    landKnockbackSpeed: 4,
    // Puck pops on a check but stays in the area — high pop speed (was
    // 6 m/s) was hurling the puck halfway across the rink, killing the
    // chance for either team to recover. Body-checks now produce
    // shorter, more contestable loose-puck moments.
    landPuckPopSpeed: 3.5,
    attackerRecoveryAfterLandTicks: 20,
    bodyCheckSprintThreshold: 0.5,
    bodyCheckMinClosingSpeed: 1.0,
    bigHitMultiplier: 1.5,
  },
  puckStrip: {
    pokePopSpeed: 4.0,
    stickLiftReach: 0.8,
    stickLiftConeDotMin: -0.5,
    stickLiftPopSpeed: 4.5,
  },
  goalie: {
    /** Goalie body collision radius — the save volume the puck must beat. */
    bodyRadius: 0.9,
    arcDepthNear: 1.0,
    arcDepthFar: 2.5,
    arcDepthPuckMaxDistance: 15,
    lateralMaxSpeed: 9,
    reactionTicks: 8,
    manualMaxSpeed: 7.0,
    manualBoxForward: 2.5,
    manualBoxHalfWidth: 3.0,
    rebound: {
      bodyDeadSpeed: 0.0,
      padReboundSpeed: 5.0,
      gloveDeadSpeed: 0.0,
      stickDeflectSpeed: 4.0,
    },
    /**
     * Slice 5 — Goalie freeze. After a save (or any goalie-body deflection)
     * the puck's `lastTouchedBy` is the goalie. If the puck is slower than
     * `freezeMaxSpeed` AND within `freezeRange` of the goalie body on a
     * subsequent tick, she covers it → stoppage at the nearest end-zone
     * dot. Tuned so a fresh pad rebound (4 m/s) doesn't trigger but a
     * slow pass that bounces off the body (1.6 m/s after restitution) does.
     */
    freezeMaxSpeed: 2.0,
    freezeRange: 1.5,
  },
  clock: {
    periodTicks: 7200,
    periodCount: 3,
    /** Pause between periods (ticks) before the next faceoff window starts. */
    periodBreakTicks: 120,
    /**
     * Faceoff window length. 180 ticks = 3 seconds. The full window
     * runs from the Stoppage / GoalCelebration / PeriodBreak resolution
     * up to the drop. Players read the visible progress bar for the
     * first 2 seconds; the last `faceoffHiddenTicks` are intentionally
     * unmarked so the timing of the commit becomes a feel mechanic.
     */
    faceoffPauseTicks: 180,
    /**
     * Last-N ticks of the Faceoff window during which the HUD progress
     * bar is hidden. 60 ticks = 1 second. The player must "feel" the
     * drop in this window; this is the difference between a clean
     * faceoff win and a contested one.
     */
    faceoffHiddenTicks: 60,
    /**
     * Number of consecutive ticks holding the SAME target required to
     * build up the full stability bonus (score = 1.0). 120 ticks = 2s.
     * Reset to 0 whenever the target changes. Stability and timing
     * scores combine additively at resolution.
     */
    faceoffStabilityFullTicks: 120,
    /**
     * Half-width (in ticks) of the timing falloff. A Pass press exactly
     * at the drop tick scores 1.0; the score falls off linearly to 0
     * over this many ticks on either side. 60 ticks = 1 second window.
     */
    faceoffTimingWindowTicks: 60,
    goalCelebrationTicks: 120,
    /**
     * Slice 8 — 3-on-3 sudden-death overtime length. 60 seconds at
     * 60Hz. First goal wins; if the timer expires tied, Slice 9
     * routes into the shootout. NHL OT is 5 minutes; we compress to
     * 60s for arcade pacing.
     */
    overtimeTicks: 3600,
    /**
     * Slice 10 — auto-pull goalie threshold. When period 3
     * remainingTicks falls to or below this and the trailing team is
     * within `autoPullMaxDeficit` goals, the trailing team's goalie
     * is pulled and the team switches to the aggressive tactic.
     */
    autoPullThresholdTicks: 3600,
    /**
     * Slice 9 — shootout post-shot resolution window. After a shot
     * is taken (ShotReleased fires), the attempt has this many ticks
     * to resolve to a Goal before being recorded as a Miss. 60 ticks
     * ≈ 1 second — typical rebound / save settle time.
     */
    shootoutPostShotTicks: 60,
    /**
     * Slice 9 — puck-must-move-forward tolerance. If the puck's
     * forward progress (signed by shooter team) drops by more than
     * this many metres from its peak this attempt, the attempt ends
     * as a Miss. Tuned to allow small drift / rebounds while
     * preventing a fumbled shooter from chasing the puck backward
     * for a second attempt.
     */
    shootoutMaxRegression: 1.5,
  },
  ai: {
    default: {
      reactionTicks: 10,
      decisionHz: 6,
      teamDecisionHz: 4,
      aimErrorDegrees: 6,
      shotSelectionSkill: 0.55,
      passSelectionSkill: 0.55,
      defensivePositioningSkill: 0.5,
      checkAggression: 0.55,
      checkTimingSkill: 0.5,
      puckControlSkill: 0.55,
      goalieReactionSkill: 0.55,
      randomness: 0.1,
      hysteresisBonus: 0.08,
      humanCarrierNoCrowdRadius: 3.0,
      passToHumanBias: 0.15,
      callForPassBias: 0.25,
    },
  },
  switching: {
    handoffTicks: 4,
    aimHandoffTicks: 2,
    manualLockoutTicks: 8,
    autoSwitchPassConfidenceThreshold: 0.75,
    candidateHysteresis: 0.18,
    roleStickinessTicks: 30,
    roleStickinessBonus: 0.15,
  },
  // Per-role × per-situation home-position anchors. See
  // packages/bots/src/positioning/home-position.ts for how these compose
  // into a target point each tick.
  //
  // Each cell carries five numbers:
  //   - laneY: lateral lane in meters, measured on the "own-left" side
  //     (positive = left from the player's own goalie POV). Mirrored once
  //     for the away team in the bot module so each player owns their
  //     own side of the ice regardless of which end they defend.
  //   - forwardOffset: meters ahead of the puck in the attack direction.
  //     Negative = behind the puck (defensive D, forecheck-trailing).
  //   - backstopFromOwnGoal: minimum distance behind which this role
  //     will not retreat, no matter how deep the puck is in our zone.
  //   - lateralPullToPuck: 0..1 weight on drifting from the lane toward
  //     puck.y. 0 = pure lane discipline, 1 = follow puck.y completely.
  //   - maxForwardFromOwnGoal: cap on how far up-ice this role advances.
  //     The most restrictive of (this cap, forwardCapFromEnemyGoal) wins.
  //
  // Situations come from packages/bots/src/situation.ts and are computed
  // each tick per team from (zone × ownership). Forwards advance further
  // in offensive-zone situations; D stay tight in defensive-zone ones.
  //
  // These values are the v1 "balanced" tactic — a sane default. Phase G
  // slice 4 will introduce alternative tactics (e.g. aggressive 2-1-2
  // forecheck, 1-3-1 trap) keyed off per-team MatchState.
  positioning: {
    anchorDeadband: 0.6,
    forwardCapFromEnemyGoal: 4,
    zones: {
      // x bound for zone classification. |puck.x| > blueLineX = offensive
      // or defensive zone (depending on which team's POV); otherwise NZ.
      // Real NHL blue lines sit ~7.6 m from center; 12 here trades a wider
      // neutral zone for clearer separation between forecheck and DZ
      // posture on a 60×30 rink.
      blueLineX: 12,
      // Hysteresis meters — puck must cross the line by this much before
      // the team's situation flips. Prevents jitter for non-chasing bots.
      zoneHysteresis: 2,
    },
    LW: {
      OffZoneOurPuck: {
        laneY: 11,
        forwardOffset: 1,
        backstopFromOwnGoal: 8,
        lateralPullToPuck: 0.3,
        maxForwardFromOwnGoal: 56,
      },
      OffZoneTheirPuck: {
        laneY: 9,
        forwardOffset: 0,
        backstopFromOwnGoal: 8,
        lateralPullToPuck: 0.2,
        maxForwardFromOwnGoal: 54,
      },
      NeutralZoneOurPuck: {
        laneY: 9,
        forwardOffset: 4,
        backstopFromOwnGoal: 6,
        lateralPullToPuck: 0.15,
        maxForwardFromOwnGoal: 44,
      },
      NeutralZoneTheirPuck: {
        laneY: 8,
        forwardOffset: -3,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.15,
        maxForwardFromOwnGoal: 36,
      },
      DefZoneOurPuck: {
        laneY: 11,
        forwardOffset: -1,
        backstopFromOwnGoal: 8,
        lateralPullToPuck: 0.1,
        maxForwardFromOwnGoal: 36,
      },
      DefZoneTheirPuck: {
        laneY: 6,
        forwardOffset: -3,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.2,
        maxForwardFromOwnGoal: 16,
      },
    },
    C: {
      OffZoneOurPuck: {
        laneY: 0,
        forwardOffset: 3,
        backstopFromOwnGoal: 6,
        lateralPullToPuck: 0.4,
        maxForwardFromOwnGoal: 56,
      },
      OffZoneTheirPuck: {
        laneY: 0,
        forwardOffset: -1,
        backstopFromOwnGoal: 6,
        lateralPullToPuck: 0.5,
        maxForwardFromOwnGoal: 54,
      },
      NeutralZoneOurPuck: {
        laneY: 0,
        forwardOffset: 2,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.4,
        maxForwardFromOwnGoal: 44,
      },
      NeutralZoneTheirPuck: {
        laneY: 0,
        forwardOffset: -3,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.5,
        maxForwardFromOwnGoal: 36,
      },
      DefZoneOurPuck: {
        laneY: 0,
        forwardOffset: -4,
        backstopFromOwnGoal: 2,
        lateralPullToPuck: 0.5,
        maxForwardFromOwnGoal: 36,
      },
      DefZoneTheirPuck: {
        laneY: 0,
        forwardOffset: -2,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.4,
        maxForwardFromOwnGoal: 16,
      },
    },
    RW: {
      OffZoneOurPuck: {
        laneY: -11,
        forwardOffset: 1,
        backstopFromOwnGoal: 8,
        lateralPullToPuck: 0.3,
        maxForwardFromOwnGoal: 56,
      },
      OffZoneTheirPuck: {
        laneY: -9,
        forwardOffset: 0,
        backstopFromOwnGoal: 8,
        lateralPullToPuck: 0.2,
        maxForwardFromOwnGoal: 54,
      },
      NeutralZoneOurPuck: {
        laneY: -9,
        forwardOffset: 4,
        backstopFromOwnGoal: 6,
        lateralPullToPuck: 0.15,
        maxForwardFromOwnGoal: 44,
      },
      NeutralZoneTheirPuck: {
        laneY: -8,
        forwardOffset: -3,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.15,
        maxForwardFromOwnGoal: 36,
      },
      DefZoneOurPuck: {
        laneY: -11,
        forwardOffset: -1,
        backstopFromOwnGoal: 8,
        lateralPullToPuck: 0.1,
        maxForwardFromOwnGoal: 36,
      },
      DefZoneTheirPuck: {
        laneY: -6,
        forwardOffset: -3,
        backstopFromOwnGoal: 4,
        lateralPullToPuck: 0.2,
        maxForwardFromOwnGoal: 16,
      },
    },
    LD: {
      OffZoneOurPuck: {
        laneY: 5,
        forwardOffset: -8,
        backstopFromOwnGoal: 3,
        lateralPullToPuck: 0.1,
        // Cap at +12 = offensive blue line (HOME). The point on a power play.
        maxForwardFromOwnGoal: 42,
      },
      OffZoneTheirPuck: {
        laneY: 4,
        forwardOffset: -10,
        backstopFromOwnGoal: 3,
        lateralPullToPuck: 0.1,
        // Hold the offensive blue line so the puck can't escape — D is the
        // last line of defense before zone exit. Cap = +12 for HOME.
        maxForwardFromOwnGoal: 42,
      },
      NeutralZoneOurPuck: {
        laneY: 4,
        forwardOffset: -6,
        backstopFromOwnGoal: 2,
        lateralPullToPuck: 0.1,
        // Trail play, never further than ~10m behind own blue line on
        // a transition. Cap = -6 for HOME (puck-side of midfield).
        maxForwardFromOwnGoal: 24,
      },
      NeutralZoneTheirPuck: {
        laneY: 4,
        forwardOffset: -10,
        backstopFromOwnGoal: 2,
        lateralPullToPuck: 0.1,
        // Hold the own blue line. Cap = -10 for HOME.
        maxForwardFromOwnGoal: 20,
      },
      DefZoneOurPuck: {
        laneY: 6,
        // Sit ~4m behind puck for breakout support — D-to-D / wing-on-wall
        // pass option, not pinned to own goal mouth.
        forwardOffset: -4,
        backstopFromOwnGoal: 1,
        lateralPullToPuck: 0.3,
        maxForwardFromOwnGoal: 18,
      },
      DefZoneTheirPuck: {
        laneY: 3,
        forwardOffset: -12,
        backstopFromOwnGoal: 1,
        lateralPullToPuck: 0.15,
        maxForwardFromOwnGoal: 12,
      },
    },
    RD: {
      OffZoneOurPuck: {
        laneY: -5,
        forwardOffset: -8,
        backstopFromOwnGoal: 3,
        lateralPullToPuck: 0.1,
        maxForwardFromOwnGoal: 42,
      },
      OffZoneTheirPuck: {
        laneY: -4,
        forwardOffset: -10,
        backstopFromOwnGoal: 3,
        lateralPullToPuck: 0.1,
        maxForwardFromOwnGoal: 42,
      },
      NeutralZoneOurPuck: {
        laneY: -4,
        forwardOffset: -6,
        backstopFromOwnGoal: 2,
        lateralPullToPuck: 0.1,
        maxForwardFromOwnGoal: 24,
      },
      NeutralZoneTheirPuck: {
        laneY: -4,
        forwardOffset: -10,
        backstopFromOwnGoal: 2,
        lateralPullToPuck: 0.1,
        maxForwardFromOwnGoal: 20,
      },
      DefZoneOurPuck: {
        laneY: -6,
        forwardOffset: -4,
        backstopFromOwnGoal: 1,
        lateralPullToPuck: 0.3,
        maxForwardFromOwnGoal: 18,
      },
      DefZoneTheirPuck: {
        laneY: -3,
        forwardOffset: -12,
        backstopFromOwnGoal: 1,
        lateralPullToPuck: 0.15,
        maxForwardFromOwnGoal: 12,
      },
    },
  },
} as const;

export type Tuning = typeof tuning;

import { createHasher, finalize, mixFloat, mixString } from "../math/Hash.js";

function hashTuningInto(value: unknown, h: ReturnType<typeof createHasher>): void {
  if (typeof value === "number") {
    mixFloat(h, value);
    return;
  }
  if (typeof value === "string") {
    mixString(h, value);
    return;
  }
  if (typeof value === "boolean") {
    mixFloat(h, value ? 1 : 0);
    return;
  }
  if (value && typeof value === "object") {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    for (const k of keys) {
      mixString(h, k);
      hashTuningInto((value as Record<string, unknown>)[k], h);
    }
  }
}

// Hash of every tuning constant. Recorded into replays so playback can detect
// when the sim has been re-balanced under a previously-captured replay.
// Stable across runs; changes whenever any value above changes.
export const TUNING_HASH: number = (() => {
  const h = createHasher();
  hashTuningInto(tuning, h);
  return finalize(h);
})();
